<template>
  <v-app>
    <!-- App Bar -->
    <v-app-bar color="primary" app elevation="2">
      <v-spacer></v-spacer>
      <div class="text-h5 font-weight-bold text-center d-flex align-center">
        <v-img :width="28"  class="ml-2" src="@/assets/soufara-white.svg"></v-img>   
        <span class="text-h5 font-weight-bold text-center">سفراء العلم - نظام إدارة الموارد البشرية</span>
      </div>
      <v-spacer></v-spacer>
      <v-menu offset-y min-width="280">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" variant="flat" color="rgba(255, 255, 255, 0.15)" class="text-none px-4" height="48">
            <v-avatar size="36" color="white" class="ml-2">
              <span class="text-primary font-weight-bold text-h6">
                {{ getUserInitials }}
              </span>
            </v-avatar>
            <div class="d-flex flex-column align-start mr-3">
              <span class="text-subtitle-2 font-weight-bold">{{ store.getters['auth/userFullName'] }}</span>
              <span class="text-caption opacity-80">{{ getUserRole }}</span>
            </div>
            <v-icon class="mr-2">mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-card>
          <v-card-text class="pa-0">
            <!-- User Info Header -->
            <div class="pa-4 bg-primary">
              <div class="d-flex align-center">
                <v-avatar size="56" color="white">
                  <span class="text-primary font-weight-bold text-h5">
                    {{ getUserInitials }}
                  </span>
                </v-avatar>
                <div class="mr-3 flex-grow-1">
                  <div class="text-subtitle-1 font-weight-bold text-white">
                    {{ store.getters['auth/userFullName'] }}
                  </div>
                  <div class="text-caption text-white opacity-80">
                    {{ store.state.auth.user?.email || store.state.auth.user?.username }}
                  </div>
                  <v-chip size="x-small" color="white" class="mt-1">
                    {{ getUserRole }}
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Menu Items -->
            <v-list density="compact" class="py-2">
              <v-list-item
                :to="profileRoute"
                prepend-icon="mdi-account-circle"
                title="الملف الشخصي"
                subtitle="عرض معلوماتك"
              ></v-list-item>
              
              <v-list-item
                @click="showChangePasswordDialog = true"
                prepend-icon="mdi-lock-reset"
                title="تغيير كلمة المرور"
                subtitle="تحديث بيانات الدخول"
              ></v-list-item>
              
              <v-divider class="my-2"></v-divider>
              
              <v-list-item
                @click="handleLogout"
                prepend-icon="mdi-logout"
                title="تسجيل الخروج"
                base-color="error"
                class="text-error"
              ></v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- Navigation Drawer -->
    <v-navigation-drawer
      app
      right
      permanent
      width="280"
      location="right"
    >

      <v-list>
        <template v-for="item in menuItems" :key="item.title">
          <!-- Group item with children -->
          <v-list-group v-if="item.children" :value="item.title">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-icon :icon="item.icon"></v-icon>
                </template>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </template>

            <v-list-item
              v-for="child in item.children"
              :key="child.title"
              :to="child.to"
              :disabled="child.disabled"
              link
              color="primary"
            >
              <template v-slot:prepend>
                <v-icon :icon="child.icon"></v-icon>
              </template>
              <v-list-item-title>{{ child.title }}</v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Regular item -->
          <v-list-item
            v-else
            :to="item.to"
            :disabled="item.disabled"
            link
            color="primary"
          >
            <template v-slot:prepend>
              <v-icon :icon="item.icon"></v-icon>
            </template>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main dir="rtl">
      <v-container fluid>
        <router-view></router-view>
      </v-container>
    </v-main>

    <!-- Change Password Dialog -->
    <v-dialog v-model="showChangePasswordDialog" max-width="550" persistent>
      <v-card elevation="8">
        <v-card-title class="bg-primary pa-5 d-flex align-center">
          <v-icon size="28" class="ml-3">mdi-lock-reset</v-icon>
          <span class="text-h5 font-weight-bold">تغيير كلمة المرور</span>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="passwordForm" v-model="passwordFormValid">
            <v-text-field
              v-model="passwordData.current_password"
              label="كلمة المرور الحالية"
              type="password"
              variant="outlined"
              density="comfortable"
              :rules="[v => !!v || 'كلمة المرور الحالية مطلوبة']"
              prepend-inner-icon="mdi-lock"
              color="primary"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="passwordData.new_password"
              label="كلمة المرور الجديدة"
              type="password"
              variant="outlined"
              density="comfortable"
              :rules="passwordRules"
              prepend-inner-icon="mdi-lock-reset"
              color="primary"
              hint="يجب أن تكون 6 أحرف على الأقل"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="confirmPassword"
              label="تأكيد كلمة المرور الجديدة"
              type="password"
              variant="outlined"
              density="comfortable"
              :rules="[
                v => !!v || 'تأكيد كلمة المرور مطلوب',
                v => v === passwordData.new_password || 'كلمات المرور غير متطابقة'
              ]"
              prepend-inner-icon="mdi-lock-check"
              color="primary"
            ></v-text-field>
          </v-form>

          <v-alert
            v-if="passwordError"
            type="error"
            variant="tonal"
            prominent
            class="mt-4"
            icon="mdi-alert-circle"
          >
            {{ passwordError }}
          </v-alert>

          <v-alert
            v-if="passwordSuccess"
            type="success"
            variant="tonal"
            prominent
            class="mt-4"
            icon="mdi-check-circle"
          >
            {{ passwordSuccess }}
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            size="large"
            @click="closePasswordDialog"
            :disabled="passwordLoading"
          >
            إلغاء
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            size="large"
            :loading="passwordLoading"
            :disabled="!passwordFormValid"
            @click="handleChangePassword"
            prepend-icon="mdi-content-save"
          >
            حفظ التغييرات
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { useStore } from 'vuex'
import authService from '@/services/auth.service'

export default {
  name: 'DefaultLayout',
  setup() {
    const store = useStore()
    const passwordForm = ref(null)
    const showChangePasswordDialog = ref(false)
    const passwordFormValid = ref(false)
    const passwordLoading = ref(false)
    const passwordError = ref(null)
    const passwordSuccess = ref(null)
    const confirmPassword = ref('')

    const passwordData = reactive({
      current_password: '',
      new_password: ''
    })

    const passwordRules = [
      v => !!v || 'كلمة المرور الجديدة مطلوبة',
      v => v.length >= 6 || 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'
    ]

    const getUserInitials = computed(() => {
      const user = store.state.auth.user
      if (!user) return ''
      
      const firstName = user.first_name_ar || user.first_name_en || ''
      const lastName = user.last_name_ar || user.last_name_en || ''
      
      if (firstName && lastName) {
        return firstName[0] + lastName[0]
      }
      
      const fullName = store.getters['auth/userFullName']
      return fullName.substring(0, 2).toUpperCase()
    })

    const getUserRole = computed(() => {
      const userType = store.getters['auth/userType']
      const roles = store.getters['auth/userRoles'] || []
      
      if (userType === 'super_admin') return 'مدير النظام'
      if (roles.includes('admin')) return 'مدير'
      if (roles.includes('hr')) return 'موارد بشرية'
      if (roles.includes('employee')) return 'موظف'
      return 'مستخدم'
    })

    const profileRoute = computed(() => {
      const userRoles = store.getters['auth/userRoles'] || []
      const isEmployee = userRoles.includes('employee') && !userRoles.includes('admin') && !userRoles.includes('hr')
      return isEmployee ? '/profile' : '/admin-profile'
    })

    const menuItems = computed(() => {
      const userRoles = store.getters['auth/userRoles'] || []
      const isAdmin = userRoles.includes('admin')
      const isHR = userRoles.includes('hr') || isAdmin
      const isEmployee = userRoles.includes('employee')
      
      if (isAdmin || isHR) {
        // Admin/HR - Full access
        return [
          {
            title: 'لوحة التحكم',
            icon: 'mdi-view-dashboard',
            to: '/'
          },
          {
            title: 'إدارة الموظفين',
            icon: 'mdi-account-group',
            to: '/employee-management'
          },
          {
            title: 'إدارة العقود',
            icon: 'mdi-file-document-multiple',
            to: '/contract-management'
          },
          {
            title: 'تقييمات الأداء',
            icon: 'mdi-star-circle',
            to: '/performance-reviews'
          },
          {
            title: 'إدارة الحضور',
            icon: 'mdi-calendar-check',
            to: '/attendance-management'
          },
          {
            title: 'طلبات الإجازات',
            icon: 'mdi-calendar-clock',
            to: '/leaves-requests'
          },
          {
            title: 'طلبات الشكاوى',
            icon: 'mdi-alert-circle',
            to: '/complaints-requests'
          },
          {
            title: 'المكافآت والجزاءات',
            icon: 'mdi-trophy-award',
            to: '/rewards-penalties'
          },
          {
            title: 'إدارة الأقسام',
            icon: 'mdi-office-building',
            to: '/departments-management'
          },
          {
            title: 'إدارة الشركات',
            icon: 'mdi-domain',
            to: '/companies-management'
          },
          {
            title: 'الإعدادات',
            icon: 'mdi-cog',
            to: '/settings'
          }
        ]
      } else if (isEmployee) {
        // Employee - Limited access
        return [
          {
            title: 'الملف الشخصي',
            icon: 'mdi-account',
            to: '/profile'
          },
          {
            title: 'حضوري',
            icon: 'mdi-calendar-check',
            to: '/my-attendance'
          },
          {
            title: 'إجازاتي',
            icon: 'mdi-calendar-clock',
            to: '/my-leaves'
          },
          {
            title: 'طلب إجازة',
            icon: 'mdi-calendar-plus',
            to: '/request-leave'
          }
        ]
      }
      
      return []
    })

    const handleLogout = async () => {
      await store.dispatch('auth/logout')
      // Force navigation to login after logout
      window.location.href = '/login'
    }

    const handleChangePassword = async () => {
      const { valid } = await passwordForm.value.validate()
      if (!valid) return

      passwordLoading.value = true
      passwordError.value = null
      passwordSuccess.value = null

      try {
        const response = await authService.changePassword(passwordData)
        
        if (response.success) {
          passwordSuccess.value = response.message || 'تم تغيير كلمة المرور بنجاح'
          
          // Reset form after 2 seconds and close dialog
          setTimeout(() => {
            closePasswordDialog()
            // Optionally logout user to re-login with new password
            // handleLogout()
          }, 2000)
        } else {
          passwordError.value = response.message || 'فشل تغيير كلمة المرور'
        }
      } catch (error) {
        passwordError.value = error.response?.data?.message || error.message || 'حدث خطأ أثناء تغيير كلمة المرور'
      } finally {
        passwordLoading.value = false
      }
    }

    const closePasswordDialog = () => {
      showChangePasswordDialog.value = false
      passwordData.current_password = ''
      passwordData.new_password = ''
      confirmPassword.value = ''
      passwordError.value = null
      passwordSuccess.value = null
      if (passwordForm.value) {
        passwordForm.value.reset()
      }
    }

    return {
      menuItems,
      handleLogout,
      store,
      showChangePasswordDialog,
      passwordForm,
      passwordFormValid,
      passwordData,
      passwordRules,
      confirmPassword,
      passwordLoading,
      passwordError,
      passwordSuccess,
      handleChangePassword,
      closePasswordDialog,
      getUserInitials,
      getUserRole,
      profileRoute
    }
  }
}
</script>

<style scoped>
/* Active navigation item styling */
:deep(.v-list-item--active) {
  background-color: rgba(131, 30, 70, 0.12) !important; 
  color: rgb(131, 30, 70) !important;
}

:deep(.v-list-item--active .v-list-item-title) {
  color: rgb(131, 30, 70) !important;
  font-weight: 600;
}

:deep(.v-list-item--active .v-icon) {
  color: rgb(131, 30, 70) !important;
}

/* Hover effect for navigation items */
:deep(.v-list-item:hover:not(.v-list-item--active)) {
  background-color: rgba(131, 30, 70, 0.04) !important;
}

/* Active group title */
:deep(.v-list-group__items .v-list-item--active) {
  background-color: rgba(131, 30, 70, 0.08) !important;
  border-right: 3px solid rgb(131, 30, 70);
}
</style> 